import { useTranslation } from "react-i18next";
import { IrrigationEventProto } from "verdi-web-client/Models/Scheduling/IrrigationEventProto";

import { useLocale } from "@/hooks/use-locale";
import { NAMESPACES } from "@/i18n/constants";
import { CLOCK_CADENCE, useWallClock } from "@/providers/wall-clock";
import { formatTimeAsHoursMinutesAndSeconds } from "@/utils/time";

const TIME_FORMAT_OPTIONS = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
} as const;

interface EventDetailsProps {
    nearestFutureEvent: IrrigationEventProto | undefined;
}

export function EventDetails({ nearestFutureEvent }: EventDetailsProps) {
    const locale = useLocale();
    const { t } = useTranslation(NAMESPACES.FEATURES.CARDS, { keyPrefix: "zone.on-demand-irrigation" });

    // Tick so we re-evaluate started text without waiting for the 5-min schedule poll.
    const nowMs = useWallClock(CLOCK_CADENCE.MINUTES_1);

    if (!nearestFutureEvent) {
        return <p className={"text-muted-foreground"}>{t("event-details.no-upcoming-event")}</p>;
    }

    const hasEnded = nearestFutureEvent.endTime.getTime() <= nowMs;
    const hasStarted = nearestFutureEvent.startTime.getTime() <= nowMs;
    const msUntilStart = nearestFutureEvent.startTime.getTime() - nowMs;

    let eventStatusText: string;
    if (hasEnded) {
        eventStatusText = t("event-details.no-upcoming-event");
    } else if (hasStarted) {
        eventStatusText = t("event-details.currently-irrigating");
    } else {
        eventStatusText = t("event-details.event-start-time", {
            time: formatTimeAsHoursMinutesAndSeconds(msUntilStart, { includeSecondsWithMinutes: false }),
        });
    }

    return (
        <div className={"flex xs:flex-row flex-col xs:items-center justify-between gap-2"}>
            <p className={"text-muted-foreground"}>{eventStatusText}</p>
            <p className={"text-muted-foreground"}>
                {nearestFutureEvent.startTime.toLocaleTimeString(locale, TIME_FORMAT_OPTIONS)} -{" "}
                {nearestFutureEvent.endTime.toLocaleTimeString(locale, TIME_FORMAT_OPTIONS)} (
                {formatTimeAsHoursMinutesAndSeconds(nearestFutureEvent.duration, { includeSecondsWithMinutes: false })})
            </p>
        </div>
    );
}
